import { _cookies } from 'utils';
import { Session } from 'redux/session/session.type';

// Session cookie changes (login/logout) don't otherwise notify React — components
// that read `retrieveUserData()` once (e.g. on mount) would never see the update.
// This mirrors the signed-thumbnail token store's subscribe/emit pattern.
type Listener = () => void;
const listeners = new Set<Listener>();

function emitSessionChange(): void {
  listeners.forEach((l) => l());
}

function subscribeToSessionChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const checkIsPageAccessAuthorized = (pagePermissions: number[], userPermissions: number[]) => {
  let isPermitted = false;

  if (!pagePermissions.length || !userPermissions.length) return isPermitted;

  for (let i = 0, size = pagePermissions.length; i < size; i++) {
    if (userPermissions.indexOf(pagePermissions[i]) >= 0) {
      isPermitted = true;
      break;
    }
  }

  return isPermitted;
};

const shouldRenewToken = (expiresAt: string) => {
  const nowSec = new Date().getTime() / 1000;
  const expiresAtSec = new Date(expiresAt).getTime() / 1000;
  const diff = expiresAtSec - nowSec;

  // refresh token when it's going to expire in less than 1 minute
  return diff < 60;
};

const storeUserData = (session: Session) => {
  // store only the fields we need — the full API response (e.g. representatives)
  // can exceed the browser's ~4KB cookie size limit
  const sessionData: Omit<Session, 'token' | 'refreshToken'> = {
    id: session.id,
    email: session.email,
    customerCode: session.customerCode,
    mustChangePassword: session.mustChangePassword,
    permissions: session.permissions,
    preferredLang: session.preferredLang,
    sessionExpiresAt: session.sessionExpiresAt,
    askAdditionalInformation: session.askAdditionalInformation,
  };

  _cookies.setCookie({ name: 'session', value: sessionData, expDays: 3 });
  emitSessionChange();

  return sessionData as Session;
};

const retrieveUserData = () => _cookies.getCookie<Session>('session');

const deleteUserData = () => {
  _cookies.deleteCookie('session');
  emitSessionChange();
};

export {
  checkIsPageAccessAuthorized,
  shouldRenewToken,
  storeUserData,
  retrieveUserData,
  deleteUserData,
  subscribeToSessionChange,
};
